const EVENT_FIELDS = Object.freeze({
  'search.results_viewed': ['result_count', 'product_count'],
  'search.result_opened': ['product_id'],
  'search.quick_shop_opened': ['product_id'],
  'size_guide.opened': ['product_id'],
  'size_guide.unit_changed': ['product_id', 'unit'],
  'product.size_selected': ['product_id', 'variant_id'],
  'product.height_selected': ['product_id', 'variant_id'],
  'size_guide.add_to_cart': ['product_id', 'variant_id'],
  'try_on.opened': ['product_id', 'variant_id'],
  'try_on.photo_selected': ['product_id', 'variant_id'],
  'try_on.generate_started': ['product_id', 'variant_id'],
  'try_on.generate_succeeded': ['product_id', 'variant_id'],
  'try_on.generate_failed': ['product_id', 'variant_id', 'reason'],
  'lookbook.opened': ['item_count'],
  'lookbook.product_opened': ['product_id'],
  'cart.item_added': ['product_id', 'variant_id', 'quantity'],
  'cart.updated': ['item_count'],
  'cart.error': ['reason'],
  'quick_cart.opened': ['item_count'],
});

const COUNT_FIELDS = new Set(['result_count', 'product_count', 'item_count', 'quantity']);
const ID_FIELDS = new Set(['product_id', 'variant_id']);
const REASONS = new Set(['endpoint', 'file_size', 'limit', 'network', 'response', 'unknown', 'validation']);

const sanitizeValue = (key, value) => {
  if (COUNT_FIELDS.has(key)) {
    const count = Number(value);
    return Number.isInteger(count) && count >= 0 && count <= 10000 ? count : undefined;
  }

  if (ID_FIELDS.has(key)) {
    const id = String(value ?? '');
    return /^[A-Za-z0-9:_-]{1,64}$/.test(id) ? id : undefined;
  }

  if (key === 'unit') {
    return value === 'cm' || value === 'in' ? value : undefined;
  }

  if (key === 'reason') {
    return REASONS.has(value) ? value : undefined;
  }

  return undefined;
};

export const sanitizeEvidence = (name, payload = {}) => {
  const fields = EVENT_FIELDS[name];
  if (!fields) return null;

  const input = payload && typeof payload === 'object' ? payload : {};
  const sanitizedPayload = {};

  fields.forEach((field) => {
    const value = sanitizeValue(field, input[field]);
    if (value !== undefined) sanitizedPayload[field] = value;
  });

  return { name, payload: sanitizedPayload };
};

export const trackEvidence = (name, payload = {}, target = globalThis.document) => {
  const evidence = sanitizeEvidence(name, payload);
  if (!evidence) return null;

  try {
    if (typeof globalThis.clarity === 'function') globalThis.clarity('event', evidence.name);
  } catch {}

  if (target && typeof globalThis.CustomEvent === 'function') {
    target.dispatchEvent(new CustomEvent('budziko:evidence:recorded', { detail: evidence }));
  }

  return evidence;
};

export const searchResultsEvidence = (dataset = {}) => {
  const input = dataset && typeof dataset === 'object' ? dataset : {};
  if (!('searchEvidence' in input)) return null;

  return sanitizeEvidence('search.results_viewed', {
    result_count: input.searchItemCount,
    product_count: input.searchProductCount,
  });
};

export const searchResultInteraction = (target) => {
  const result = target?.closest?.('[data-search-product-id]');
  if (!result) return null;

  let name;
  if (target.matches?.('[data-quick-add]')) {
    name = 'search.quick_shop_opened';
  } else if (target.matches?.('a[href]')) {
    name = 'search.result_opened';
  } else {
    return null;
  }

  return sanitizeEvidence(name, { product_id: result.dataset?.searchProductId });
};

const extractCartCount = (cart) => cart?.item_count ?? cart?.itemCount;

const contextFromElement = (element) => {
  const variantRoot = element?.closest?.('[data-product-variants]');
  const root = variantRoot
    || element?.closest?.('[data-product-id]')
    || document.querySelector('[data-virtual-try-on]');
  return {
    product_id: root?.dataset?.productId,
    variant_id: variantRoot?.dataset?.currentVariantId
      || root?.dataset?.variantId
      || root?.dataset?.initialVariantId,
  };
};

const SEARCH_SECTION_SELECTOR = '[data-section-type="search"]';
const SEARCH_EVIDENCE_SELECTOR = '[data-search-evidence]:not([data-evidence-ready])';
const searchObservers = new WeakMap();
let browserListenersBound = false;

const matchingElements = (scope, selector) => {
  const matches = [];
  if (scope?.matches?.(selector)) matches.push(scope);
  scope?.querySelectorAll?.(selector).forEach((element) => matches.push(element));
  return matches;
};

export const syncSearchResultSummary = (marker) => {
  const section = marker?.closest?.(SEARCH_SECTION_SELECTOR);
  if (!section) return false;

  const headingLabel = section.querySelector?.('[data-search-heading-label]');
  const resultSummary = section.querySelector?.('[data-search-result-summary]');
  const nextHeadingLabel = marker.querySelector?.('[data-search-heading-label-source]')?.textContent?.trim();
  const nextResultSummary = marker.querySelector?.('[data-search-result-summary-source]')?.textContent?.trim();
  let updated = false;

  if (headingLabel && typeof nextHeadingLabel === 'string') {
    headingLabel.textContent = `${nextHeadingLabel}:`;
    updated = true;
  }
  if (resultSummary && typeof nextResultSummary === 'string') {
    resultSummary.textContent = nextResultSummary;
    updated = true;
  }

  return updated;
};

export const scanSearchResultViews = (scope, record = trackEvidence) => {
  let tracked = 0;

  matchingElements(scope, SEARCH_EVIDENCE_SELECTOR).forEach((marker) => {
    marker.dataset.evidenceReady = 'true';
    syncSearchResultSummary(marker);
    const evidence = searchResultsEvidence(marker.dataset);
    if (!evidence) return;

    record(evidence.name, evidence.payload);
    tracked += 1;
  });

  return tracked;
};

export const releaseSearchResultObserver = (section, observers = searchObservers) => {
  const observer = observers.get(section);
  if (!observer) return false;

  observer.disconnect();
  observers.delete(section);
  return true;
};

const observeSearchSections = (scope) => {
  matchingElements(scope, SEARCH_SECTION_SELECTOR).forEach((section) => {
    if (searchObservers.has(section)) return;

    const observer = new MutationObserver(() => scanSearchResultViews(section));
    observer.observe(section, { childList: true, subtree: true });
    searchObservers.set(section, observer);
  });
};

const disconnectSearchObservers = (scope) => {
  matchingElements(scope, SEARCH_SECTION_SELECTOR).forEach((section) => {
    releaseSearchResultObserver(section);
  });
};

const bindBrowserEvidenceListeners = () => {
  if (browserListenersBound) return;
  browserListenersBound = true;
  const productsWithGuideOpened = new Set();

  window.BudzikoEvidence = Object.freeze({ initialized: true, track: trackEvidence });

  document.addEventListener('flu:product:variant-change', (event) => {
    const variant = event.detail?.variant;
    if (!variant?.id) return;

    const productId = String(variant.product_id || variant.productId || '');
    const roots = [...document.querySelectorAll('[data-product-variants]')];
    const matchingRoots = productId
      ? roots.filter((root) => root.dataset.productId === productId)
      : roots.length === 1 ? roots : [];

    matchingRoots.forEach((root) => {
      root.dataset.currentVariantId = String(variant.id);
    });
  });

  document.addEventListener('click', (event) => {
    const target = event.target.closest?.('a, button');
    if (!target) return;

    const searchInteraction = searchResultInteraction(target);

    if (target.matches('[data-open-try-on]')) {
      trackEvidence('try_on.opened', contextFromElement(target));
    } else if (target.matches('[data-lookbook-open]')) {
      trackEvidence('lookbook.opened', { item_count: target.dataset.itemCount });
    } else if (target.matches('[data-size-fit-open]')) {
      const context = contextFromElement(target);
      if (context.product_id) productsWithGuideOpened.add(context.product_id);
      trackEvidence('size_guide.opened', context);
    } else if (target.closest('[data-fit-option]')) {
      if (target.matches('button.product-form__chip') && target.style.display === 'none') return;
      const option = target.closest('[data-fit-option]').dataset.fitOption;
      if (option === 'size' || option === 'height') {
        trackEvidence('product.' + option + '_selected', contextFromElement(target));
      }
    } else if (target.closest('[data-lookbook-product-id]')) {
      trackEvidence('lookbook.product_opened', {
        product_id: target.closest('[data-lookbook-product-id]').dataset.lookbookProductId,
      });
    } else if (searchInteraction) {
      trackEvidence(searchInteraction.name, searchInteraction.payload);
    }
  });

  document.addEventListener('change', (event) => {
    const control = event.target.closest?.('[data-fit-option]');
    if (!control || !event.target.matches('select')) return;
    const option = control.dataset.fitOption;
    if (option === 'size' || option === 'height') {
      trackEvidence('product.' + option + '_selected', contextFromElement(event.target));
    }
  });

  document.addEventListener('flu:cart:item-added', (event) => {
    const product = event.detail?.product || {};
    trackEvidence('cart.item_added', {
      product_id: product.product_id || product.productId || product.id,
      variant_id: product.variant_id || product.variantId,
      quantity: product.quantity || 1,
    });
    const productId = String(product.product_id || product.productId || product.id || '');
    if (productsWithGuideOpened.has(productId)) {
      trackEvidence('size_guide.add_to_cart', {
        product_id: productId,
        variant_id: product.variant_id || product.variantId,
      });
      productsWithGuideOpened.delete(productId);
    }
  });

  document.addEventListener('flu:cart:updated', (event) => {
    trackEvidence('cart.updated', { item_count: extractCartCount(event.detail?.cart) });
  });

  document.addEventListener('flu:cart:error', () => {
    trackEvidence('cart.error', { reason: 'validation' });
  });

  document.addEventListener('flu:quick-cart:open', (event) => {
    trackEvidence('quick_cart.opened', { item_count: extractCartCount(event.detail?.cart) });
  });
};

const initializeBrowserEvidence = (scope = document) => {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  bindBrowserEvidenceListeners();
  scanSearchResultViews(scope);
  observeSearchSections(scope);
};

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeBrowserEvidence(document), { once: true });
  } else {
    initializeBrowserEvidence();
  }

  document.addEventListener('shopify:section:load', (event) => initializeBrowserEvidence(event.target));
  document.addEventListener('shopify:section:unload', (event) => disconnectSearchObservers(event.target));
}
